// BroadPicture.jsx
// DAS-2550: read-only panel for the top-down "Broad Picture" (macro regime).
// Shows the multi-axis regime, narrative, tailwinds/headwinds, what changed
// since last run, the external SOURCES used (clickable), and the LLM COST of
// generating it. Reads GET /api/broad_picture (JWT via functions/api).
//
// Mount it wherever market/macro context belongs, e.g. in the dashboard:
//   import BroadPicture from './components/BroadPicture';
//   <BroadPicture />

import React, { useEffect, useState } from 'react';
import { Card, Tag, Descriptions, Typography, Spin, Alert, Tooltip, Button, message, Modal, Space, Popconfirm } from 'antd';
// DAS-2645: full-view modal (MUI, matching the rest of the app's dialogs) + reuse of
// the existing PDF export component and the /jira/create_ticket endpoint.
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button as MuiButton } from '@mui/material';
// DAS-2645: dedicated PDF export (portrait, width-fit, multi-page, dated filename).
// The shared DownloadPDF squeezes everything onto one landscape page → tiny text.
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import api from '../functions/api';
import MarketWeatherBadge from './MarketWeatherBadge'; // DAS-2765

const { Paragraph, Text, Title } = Typography;

const AXIS_LABELS = {
  growth: 'Growth',
  inflation: 'Inflation',
  policy_rates: 'Policy / Rates',
  valuation: 'Valuation',
  volatility: 'Volatility',
  commodity_geopolitics: 'Commodity / Geopolitics',
};

// Colour hint per axis state (best-effort; unknown states fall back to default).
const STATE_COLOR = {
  expansion: 'green', slowing: 'orange', contraction: 'red',
  disinflation: 'green', sticky: 'orange', accelerating: 'red',
  easing: 'green', 'on-hold': 'blue', hiking: 'red',
  calm: 'green', moderate: 'blue', stressed: 'orange', panic: 'red',
  benign: 'green', elevated: 'orange', shock: 'red',
  cheap: 'green', fair: 'blue', rich: 'red',
  unknown: 'default',
};

const fmtEpoch = (epoch) => {
  if (!epoch) return '—';
  try { return new Date(epoch * 1000).toLocaleString(); } catch { return String(epoch); }
};

// DAS-2645: filename-safe generation timestamp, e.g. 2026-07-13_1052.
const pdfStamp = (epoch) => {
  const d = epoch ? new Date(epoch * 1000) : new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`;
};

// DAS-2633: a wind's validity horizon — how long the read typically stays valid.
// Shorter horizon = decays faster, so read it sooner. Colour hints the urgency.
const HORIZON_COLOR = {
  hours: 'red', days: 'volcano', 'days-weeks': 'orange',
  weeks: 'gold', 'weeks-months': 'blue', months: 'green',
};

// DAS-2801: antd tooltips default to z-index 1070, the MUI Dialog sits at 1300 —
// so every hover inside the full-view modal rendered BEHIND it and was unreadable.
// One constant, applied to every Tooltip in this panel (harmless outside the modal).
const TIP_Z = 2000;

// DAS-2801: one place for "what does this word actually mean". Sourced from
// pythonscripts/DR_Broad_Picture.py (COMPUTED_AXES + AXIS_HORIZON) — keep in sync
// with the thresholds there rather than inventing numbers here.
const AXIS_HELP = {
  growth: {
    what: 'Is the economy growing or slowing down?',
    from: 'GDP growth, change in unemployment, the yield curve, forward-earnings revisions (1 and 4 week), analyst target-price revisions, market-wide earnings surprises, the advance/decline line, the % of stocks above their 50-day average, and how many of the 11 sectors are trending up.',
    states: 'expansion (growing) · slowing (the fallback when nothing crosses a threshold) · contraction (shrinking)',
  },
  inflation: {
    what: 'Are prices speeding up or cooling down?',
    from: 'consumer price inflation, year over year.',
    states: 'disinflation (below 2.5%) · sticky (fallback) · accelerating (above 4%)',
  },
  policy_rates: {
    what: 'Which way are central banks leaning — cutting or hiking?',
    from: 'the 10-day move in the US 2-year and 10-year government bond yields. The 2-year counts double: it is the tenor that tracks rate expectations.',
    states: 'easing (yields falling more than 5%) · on-hold (fallback) · hiking (yields rising more than 5%)',
  },
  valuation: {
    what: 'Is the market as a whole cheap or expensive?',
    from: 'the Buffett indicator — total stock-market value divided by GDP.',
    states: 'cheap (below 90) · fair (fallback) · rich (above 120)',
  },
  volatility: {
    what: 'How stressed is the market right now?',
    from: 'the VIX, the put/call ratio, the fear & greed index, the bull-bear spread, and S&P volume versus its normal level.',
    states: 'calm · moderate (fallback) · panic',
  },
  commodity_geopolitics: {
    what: 'Oil prices and geopolitical risk.',
    from: 'live web research, not internal data — which is why it carries the "web · unverified" tag.',
    states: 'benign · elevated · shock',
  },
};

// DAS-2801: why each axis has the horizon it has. The AXIS horizon is deterministic
// (fixed per axis, from the cadence of its inputs); a WIND's horizon is assigned by
// the analysis itself. Different provenance — say which, so neither is over-trusted.
const AXIS_HORIZON_WHY = {
  growth: 'monthly and quarterly data (GDP, payrolls, earnings revisions)',
  inflation: 'monthly inflation prints',
  policy_rates: 'the next central-bank meeting',
  valuation: 'market value versus GDP, which shifts over months and quarters',
  volatility: 'the VIX and put/call, which move day to day',
  commodity_geopolitics: 'oil and geopolitics, which are event-driven',
};

const HORIZON_PLAIN = {
  hours: 'hours', days: 'about a day', 'days-weeks': 'a few days to a couple of weeks',
  weeks: 'a few weeks', 'weeks-months': 'several weeks to a few months', months: 'months',
};

// DAS-2801: explain a state chip — what the word means AND what the % is. The
// percentage is NOT a probability: it is the share of the voting weight that
// landed on the winning state.
const axisValueHelp = (ax, a) => {
  const help = AXIS_HELP[ax];
  const lines = [];
  if (help) {
    lines.push(`${AXIS_LABELS[ax]} — ${help.what}`);
    lines.push('');
    lines.push(`Possible readings: ${help.states}`);
    lines.push(`Measured from: ${help.from}`);
  }
  if (!a) return lines.join('\n');
  lines.push('');
  if (a.no_signal) {
    lines.push('"no clear signal" means every input stayed inside its thresholds, so');
    lines.push('none of them voted. Rather than show the fallback reading at 100% —');
    lines.push('which would look like certainty — nothing is claimed.');
    if (a.n_signals != null) {
      lines.push(`(${a.n_signals} input${a.n_signals === 1 ? '' : 's'} checked, none crossed a threshold.)`);
    }
  } else {
    const pct = a.confidence != null ? `${Math.round(a.confidence * 100)}%` : null;
    if (pct) {
      lines.push(`The ${pct} is NOT a probability. It is how much of the total voting`);
      lines.push(`weight landed on "${a.state}" — so ${pct} of the weight pointed this way`);
      lines.push('and the rest pointed elsewhere. Higher = the inputs agree more.');
    }
    if (a.n_signals != null && a.n_fired != null) {
      lines.push(`${a.n_fired} of ${a.n_signals} inputs actually crossed a threshold; the others`);
      lines.push('defaulted to the middle reading.');
    }
  }
  return lines.join('\n');
};

// DAS-2801: small ⓘ that carries an explanation, above the modal.
const InfoTip = ({ title, style = {} }) => (
  <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 460 }}
           title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{title}</span>}>
    <span style={{ color: '#1677ff', fontSize: 12, marginLeft: 5, cursor: 'help', ...style }}>ⓘ</span>
  </Tooltip>
);
const fmtPct = (c) =>
  (c !== undefined && c !== null) ? `${Math.round(c * 100)}%` : '—';

// DAS-2635: plain-language strength from the DAS-2634 roll-up fields — how many
// of an axis's signals actually fired, not a bare "%". Falls back gracefully for
// pre-DAS-2634 rows that lack the counts.
const strengthText = (a) => {
  if (!a) return '';
  if (a.no_signal) {
    // chip already says "no clear signal" — explain why instead of repeating it
    if (a.n_signals != null) {
      return a.n_signals === 1 ? 'single reading, no threshold hit' : `${a.n_signals} signals, none fired`;
    }
    return '';
  }
  if (a.n_signals != null && a.n_fired != null) {
    if (a.n_signals === 1) return 'single reading';
    return `${a.n_fired} of ${a.n_signals} signals fired`;
  }
  // Model (web) axes: the "web · unverified" badge + rationale carry it. Don't
  // show the LLM's self-confidence as "% agreement" — there are no signals to agree.
  if (a.source === 'model') return '';
  if (a.confidence != null) return `${Math.round(a.confidence * 100)}% agreement`;
  return '';
};

// DAS-2635: agree/disagree vs the Market Status equity regime (from DAS-2634).
const AGREEMENT = {
  agree: { color: 'success', mark: '✓', text: 'matches Market Status',
    tip: 'This axis agrees with the equity regime shown in Market Status.' },
  disagree: { color: 'warning', mark: '⚠', text: 'differs from Market Status',
    tip: 'This axis disagrees with the equity regime in Market Status — worth a look.' },
};

// DAS-2566: strip Claude web_search citation markup (<cite index="…">…</cite>)
// that can appear in already-stored rows, keeping the inner text. New rows are
// also cleaned server-side in DR_Broad_Picture.parse_picture.
const stripCitations = (s) =>
  (typeof s === 'string'
    ? s.replace(/<\/?cite\b[^>]*>/gi, '').replace(/[ \t]{2,}/g, ' ').trim()
    : s);

const BroadPicture = ({ onOpenMarketStatus }) => {
  const [data, setData] = useState(null);
  const [prev, setPrev] = useState(null);   // DAS-2633: previous run, for the Prev → Now diff
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  // DAS-2645: full-view modal + "create Jira ticket" sub-dialog state.
  const [modalOpen, setModalOpen] = useState(false);
  const [jiraOpen, setJiraOpen] = useState(false);
  const [jiraDesc, setJiraDesc] = useState('');
  const [jiraBusy, setJiraBusy] = useState(false);
  // DAS-2664: forward-risk watchlist (registry + latest scores). New risks are
  // AUTO-ACTIVATED by the daily run (DAS-2663) — this panel is the owner's
  // supervision surface, incl. the retire (kill) action.
  const [scen, setScen] = useState(null);

  const loadScenarios = () => {
    api.get('/scenarios')
      .then((res) => setScen(res.data || null))
      .catch(() => setScen(null));   // endpoint absent (pre-restart) → section hides
  };

  const decideScenario = async (id, decision) => {
    try {
      await api.post('/scenarios/decide', { id, decision });
      message.success(decision === 'retire' ? 'Risk retired' : `Risk ${decision}ed`);
      loadScenarios();
    } catch (err) {
      message.error('Action failed: ' + (err?.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    let alive = true;
    // DAS-2633: pull the last 2 runs so we can show a structured diff vs the
    // previous Broad Picture (history is already stored server-side).
    api.get('/broad_picture?history=2')
      .then((res) => {
        if (!alive) return;
        setData(res.data?.latest || null);
        setPrev(res.data?.history?.[1] || null);
      })
      .catch((err) => { if (alive) setError(err?.response?.data?.error || err.message); })
      .finally(() => { if (alive) setLoading(false); });
    loadScenarios();   // DAS-2664: forward-risk watchlist
    return () => { alive = false; };
  }, []);

  // DAS-2550: on-demand generation. Fires the background job, then polls
  // GET /api/broad_picture until a newer run (higher epoch) appears.
  const runGeneration = async () => {
    const startEpoch = Number(data?.epoch || 0);
    setGenerating(true);
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    try {
      await api.post('/broad_picture/generate');
      // DAS-2677: generation grew past 5 minutes as the risk watchlist grew
      // (each active risk is re-researched per run). Poll up to ~11 min to
      // outlast the backend's 600s agent timeout instead of giving up first.
      for (let i = 0; i < 130; i++) {
        await sleep(5000);
        try {
          const res = await api.get('/broad_picture?history=2');
          const latest = res.data?.latest;
          if (latest && Number(latest.epoch) > startEpoch) {
            setData(latest);
            setPrev(res.data?.history?.[1] || null);
            message.success('Broad Picture regenerated');
            return;
          }
        } catch (_) { /* transient — keep polling */ }
      }
      message.warning('Generation is taking unusually long (>11 min) — it may have failed on the server. Check Script Reporting for DR_Broad_Picture.py.');
    } catch (err) {
      message.error('Could not start generation: ' + (err?.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerate = () => {
    Modal.confirm({
      title: 'Generate a fresh Broad Picture?',
      content: 'Runs live web research now (about a minute) and incurs an LLM cost (~€0.43 on the API key, or your subscription credit once enabled).',
      okText: 'Generate now',
      cancelText: 'Cancel',
      onOk: () => { runGeneration(); },   // not returned → dialog closes immediately; button shows "Generating…"
    });
  };

  const generateButton = (
    <Button size="small" type="primary" loading={generating} onClick={handleGenerate}>
      {generating ? 'Generating…' : 'Generate now'}
    </Button>
  );

  // DAS-2645: portrait, width-fitted, multi-page PDF of the full-view modal content,
  // filename stamped with the generation date/time. Readable (unlike the shared
  // one-landscape-page export). jsPDF + html2canvas are already app deps.
  const handleDownloadPDF = async () => {
    const el = document.getElementById('broad-picture-modal-content');
    if (!el) return;
    try {
      const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', windowWidth: el.scrollWidth });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;                          // full page width = largest readable text
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {                      // paginate a tall image across pages
        position = heightLeft - imgH;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      pdf.save(`Broad_Picture_${pdfStamp(data.epoch)}.pdf`);
    } catch (e) {
      message.error('PDF export failed: ' + (e?.message || e));
    }
  };

  // DAS-2645: create a Jira ticket from this Broad Picture, reusing the existing
  // /api/jira/create_ticket endpoint (DAS-1603). Summary = the regime label; body =
  // the owner's optional note + the prompt-ready Broad Picture text.
  const handleCreateJira = async () => {
    if (!data) return;
    setJiraBusy(true);
    try {
      const label = stripCitations(data.regime_label) || 'Macro regime';
      const summary = `Broad Picture — ${label}`.slice(0, 240);
      const bpText = stripCitations(data.prompt_ready_text || data.narrative || '');
      const note = (jiraDesc || '').trim();
      const description = [
        note || null,
        note ? '\n---\n' : null,
        `Broad Picture (generated ${fmtEpoch(data.epoch)}):`,
        bpText,
      ].filter(Boolean).join('\n');
      const resp = await api.post('/jira/create_ticket', {
        summary, description, issue_type: 'Task', priority: 'Medium', labels: ['broad-picture'],
      });
      if (!resp.data?.success || !resp.data?.key) {
        throw new Error(resp.data?.error || 'Failed to create ticket');
      }
      message.success(
        <span>Created <a href={resp.data.url} target="_blank" rel="noopener noreferrer">{resp.data.key}</a></span>
      );
      setJiraOpen(false);
      setJiraDesc('');
    } catch (err) {
      message.error('Could not create ticket: ' + (err?.response?.data?.error || err.message));
    } finally {
      setJiraBusy(false);
    }
  };

  if (loading) return <Spin tip="Loading Broad Picture…" style={{ display: 'block', margin: '24px auto' }} />;
  if (error) return <Alert type="error" message="Could not load Broad Picture" description={error} showIcon />;
  if (!data) return (
    <Alert type="info" showIcon message="No Broad Picture generated yet." action={generateButton} />
  );

  const axes = data.axes_json || {};
  const impl = data.implications_json || {};
  const sources = data.sources_json || [];
  const ctx = data.context_json || {};   // DAS-2660: non-axis context reads (institutional)
  const prevImpl = (prev && prev.implications_json) || {};   // DAS-2801: winds diff

  // DAS-2801: which winds are new since the previous run, and which dropped off.
  // Matched on the `area` text (case/space-insensitive) — the analysis rewrites the
  // "why" every run, so only the area is stable enough to diff on.
  const windKey = (w) => String((w && w.area) || '').trim().toLowerCase();
  const windDiff = (nowList, prevList) => {
    const prevKeys = new Set((prevList || []).map(windKey).filter(Boolean));
    const nowKeys = new Set((nowList || []).map(windKey).filter(Boolean));
    return {
      isNew: (w) => prevKeys.size > 0 && !prevKeys.has(windKey(w)),
      dropped: (prevList || []).filter((w) => windKey(w) && !nowKeys.has(windKey(w))),
    };
  };
  const tailDiff = windDiff(impl.tailwinds, prevImpl.tailwinds);
  const headDiff = windDiff(impl.headwinds, prevImpl.headwinds);

  // DAS-2801: a wind's horizon is assigned by the ANALYSIS (unlike an axis horizon,
  // which is fixed per axis) — say so, so it is read with the right confidence.
  const windHorizonTip = (h) =>
    `Stays valid for roughly ${HORIZON_PLAIN[h] || h}.\n\n` +
    'How this is set: the analysis tags each tailwind/headwind with its own shelf life, ' +
    'picked from a fixed list (hours, days, days-weeks, weeks, weeks-months, months); ' +
    'anything outside that list is dropped rather than shown.\n\n' +
    'It is a judgement by the analysis, not a calculation — unlike the ⏱ on the ' +
    'axis rows below, which is fixed per axis by how often its data updates.';

  // DAS-2801: render one tailwind/headwind list item, with the new-since-previous
  // marker and an explained horizon badge.
  const renderWind = (w, i, diff) => (
    <li key={i}>
      <b>{stripCitations(w.area)}</b>{w.why ? ` — ${stripCitations(w.why)}` : ''}
      {w.horizon ? (
        <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                 title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{windHorizonTip(w.horizon)}</span>}>
          <Tag color={HORIZON_COLOR[w.horizon] || 'default'} style={{ marginLeft: 6, cursor: 'help' }}>⏱{w.horizon}</Tag>
        </Tooltip>
      ) : null}
      {diff.isNew(w) && (
        <Tooltip zIndex={TIP_Z} title="Not in the previous run — this wind is new.">
          <Tag color="gold" style={{ marginLeft: 4, fontSize: 10, cursor: 'help' }}>new</Tag>
        </Tooltip>
      )}
    </li>
  );

  // DAS-2801: winds that were there last run and are gone now — the other half of
  // "what changed", which was previously invisible.
  const renderDropped = (dropped) => (dropped.length > 0 ? (
    <div style={{ marginTop: 4, fontSize: 12 }}>
      <Text type="secondary">
        Gone since previous: {dropped.map((w) => stripCitations(w.area)).join(', ')}
      </Text>
    </div>
  ) : null);

  // DAS-2645: the whole body is rendered by this helper so it can appear both
  // inline (compact) and in the full-view modal (expanded=true → always show the
  // per-axis explanatory text; inline shows it on hover only).
  const renderContent = (expanded) => (
    <>
      {/* DAS-2765: mechanical market weather next to the LLM narrative — the
          price-based counterpart the story can be checked against */}
      <div style={{ marginBottom: 8 }}>
        <MarketWeatherBadge />
      </div>
      {/* DAS-2801: order — what CHANGED comes first (that is what the owner reads
          the panel for), then the winds it drives, then the narrative, then the
          per-axis detail, then the rest (context / risks / sources / cost / prompt).
          Supersedes the DAS-2645 narrative-first order. */}

      {/* DAS-2633: structured diff vs the previous run (Prev → Now) */}
      {prev && prev.axes_json && (
        <div style={{ marginBottom: 12 }} data-testid="bp-change-since-previous">
          <Title level={5} style={{ marginBottom: 4 }}>
            Change since previous
            <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>
              {' '}(prev run {fmtEpoch(prev.epoch)})
            </Text>
            <InfoTip title={'How the six dials moved between the previous run and this one.\n\nEach row: the reading last time → the reading now. A highlighted row means the reading changed state, or its agreement moved by 10 points or more.\n\nHover any dial name or chip for what it measures and what the number means.'} />
          </Title>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                  <th style={{ padding: '2px 10px 2px 0' }}>Axis</th>
                  <th style={{ padding: '2px 10px' }}>Previous</th>
                  <th style={{ padding: '2px 10px' }}></th>
                  <th style={{ padding: '2px 10px' }}>Now</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(AXIS_LABELS).map((ax) => {
                  const now = axes[ax] || {};
                  const was = (prev.axes_json || {})[ax] || {};
                  const stateChanged = was.state && now.state && was.state !== now.state;
                  const confDelta = (was.confidence != null && now.confidence != null)
                    ? Math.round((now.confidence - was.confidence) * 100) : null;
                  const changed = stateChanged || (confDelta !== null && Math.abs(confDelta) >= 10);
                  return (
                    <tr key={ax} style={{ borderBottom: '1px solid #f5f5f5', background: changed ? 'rgba(250,173,20,0.10)' : 'transparent' }}>
                      {/* DAS-2801: the axis name explains what the dial measures… */}
                      <td style={{ padding: '2px 10px 2px 0', whiteSpace: 'nowrap' }}>
                        <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 460 }}
                                 title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{axisValueHelp(ax, null)}</span>}>
                          <span style={{ cursor: 'help', borderBottom: '1px dotted #bbb' }}>{AXIS_LABELS[ax]}</span>
                        </Tooltip>
                      </td>
                      {/* …and each chip explains its own reading + what the % is */}
                      <td style={{ padding: '2px 10px', whiteSpace: 'nowrap' }}>
                        <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 460 }}
                                 title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{axisValueHelp(ax, was)}</span>}>
                          <Tag color={STATE_COLOR[was.state] || 'default'} style={{ marginRight: 0, cursor: 'help' }}>
                            {was.no_signal ? 'no clear signal' : `${was.state || '—'} ${fmtPct(was.confidence)}`}
                          </Tag>
                        </Tooltip>
                      </td>
                      <td style={{ padding: '2px 6px', color: changed ? '#d46b08' : '#bbb' }}>→</td>
                      <td style={{ padding: '2px 10px', whiteSpace: 'nowrap' }}>
                        <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 460 }}
                                 title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{axisValueHelp(ax, now)}</span>}>
                          <Tag color={now.no_signal ? 'default' : (STATE_COLOR[now.state] || 'default')} style={{ marginRight: 6, cursor: 'help' }}>
                            {now.no_signal ? 'no clear signal' : `${now.state || '—'} ${fmtPct(now.confidence)}`}
                          </Tag>
                        </Tooltip>
                        {/* DAS-2801: exactly ONE trailing note. It used to print the delta
                            AND "steady" back to back with no space ("(-4 pts)steady").
                            Suppressed entirely for a no-signal axis — its 100% is a
                            fallback, so a "move" against it is not a real move. */}
                        {!now.no_signal && (() => {
                          const small = confDelta === null || Math.abs(confDelta) < 10;
                          if (confDelta !== null && confDelta !== 0) {
                            return (
                              <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 420 }}
                                       title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                                         {`Agreement moved ${confDelta > 0 ? 'up' : 'down'} ${Math.abs(confDelta)} points since the previous run.\n\nThat is the share of voting weight behind the reading — not a probability. A move under 10 points is treated as noise and shown as "steady".`}
                                       </span>}>
                                <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
                                  ({confDelta > 0 ? '+' : ''}{confDelta} pts){!stateChanged && small ? ' · steady' : ''}
                                </Text>
                              </Tooltip>
                            );
                          }
                          if (!stateChanged && small) {
                            return <Text type="secondary" style={{ fontSize: 12 }}>steady</Text>;
                          }
                          return null;
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tailwinds / headwinds — DAS-2801: kept directly under "Change since
          previous", and now carrying their own since-previous markers. */}
      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <Title level={5} style={{ color: 'green', marginBottom: 4 }}>
            Tailwinds
            <InfoTip title={'What is pushing markets FORWARD right now, as read by the analysis.\n\nEach entry names an area, why it helps, and ⏱ how long that read stays valid. A gold "new" tag means it was not in the previous run; anything that dropped off is listed underneath.'} />
          </Title>
          <ul style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', marginBottom: 4 }}>
            {(impl.tailwinds || []).map((t, i) => renderWind(t, i, tailDiff))}
          </ul>
          {renderDropped(tailDiff.dropped)}
        </div>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <Title level={5} style={{ color: '#c0392b', marginBottom: 4 }}>
            Headwinds
            <InfoTip title={'What is holding markets BACK right now, as read by the analysis.\n\nEach entry names an area, why it hurts, and ⏱ how long that read stays valid. A gold "new" tag means it was not in the previous run; anything that dropped off is listed underneath.'} />
          </Title>
          <ul style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', marginBottom: 4 }}>
            {(impl.headwinds || []).map((h, i) => renderWind(h, i, headDiff))}
          </ul>
          {renderDropped(headDiff.dropped)}
        </div>
      </div>

      {/* DAS-2801: the written story now follows the numbers it describes,
          under its own heading (owner request: the block needs a title). */}
      {data.narrative && (
        <div style={{ marginTop: 12 }}>
          <Title level={5} style={{ marginBottom: 4 }}>Situation</Title>
          <Paragraph style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {stripCitations(data.narrative)}
          </Paragraph>
        </div>
      )}

      {/* DAS-2635 axis rows (DAS-2645: moved below narrative/diff/winds) — each reading
          shows its backing signals, plain-language strength, ⏱ horizon and Market-Status
          agreement. Inline shows the detail on hover; the modal (expanded) always shows it. */}
      <div style={{ marginTop: 16, marginBottom: 6 }}>
        {Object.keys(AXIS_LABELS).map((ax) => {
          const a = axes[ax] || {};
          const isModel = a.source === 'model';
          const noSignal = !!a.no_signal;
          const chipColor = noSignal ? 'default' : (STATE_COLOR[a.state] || 'default');
          const contribs = a.contributors || [];
          const agree = AGREEMENT[a.market_status] || null;
          const strength = strengthText(a);
          const detail = [
            stripCitations(a.rationale) || '',
            contribs.length ? `from: ${contribs.map((c) => `${c.vector} ${c.value}`).join(', ')}` : '',
          ].filter(Boolean).join('\n\n');
          return (
            <div key={ax} style={{
              display: 'grid', gridTemplateColumns: '155px minmax(0,1fr) 140px', gap: 10,
              alignItems: 'start', padding: '8px 0', borderTop: '1px solid #f0f0f0',
            }}>
              {/* state */}
              <div>
                <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 460 }}
                         title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{axisValueHelp(ax, a)}</span>}>
                  <Tag color={chipColor} style={{ marginRight: 0, whiteSpace: 'normal', cursor: 'help' }}>
                    {AXIS_LABELS[ax]}: <b>{noSignal ? 'no clear signal' : (a.state || 'unknown')}</b>
                  </Tag>
                </Tooltip>
                {isModel && (
                  <div style={{ marginTop: 4 }}>
                    <Tooltip zIndex={TIP_Z} title="Set from live web research — not verified against internal data.">
                      <Tag style={{ fontSize: 11, marginRight: 0, cursor: 'help' }}>web · unverified</Tag>
                    </Tooltip>
                  </div>
                )}
              </div>
              {/* backing signals + strength: inline shows strength + ⓘ (detail on hover);
                  the modal (expanded) always shows the full detail. */}
              <div style={{ fontSize: 13, minWidth: 0 }}>
                {expanded ? (
                  <>
                    {strength && <span style={{ fontWeight: 500 }}>{strength}</span>}
                    {a.rationale && (
                      <div><Text type="secondary" style={{ fontSize: 13 }}>{stripCitations(a.rationale)}</Text></div>
                    )}
                    {contribs.length > 0 && (
                      <div style={{ marginTop: 2, overflowWrap: 'anywhere' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          from: {contribs.map((c) => `${c.vector} ${c.value}`).join(', ')}
                        </Text>
                      </div>
                    )}
                  </>
                ) : (
                  <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 460 }}
                           title={detail ? <span style={{ whiteSpace: 'pre-line' }}>{detail}</span> : ''}>
                    <span style={{ cursor: detail ? 'help' : 'default' }}>
                      {strength && <span style={{ fontWeight: 500 }}>{strength}</span>}
                      {detail && <span style={{ color: '#1677ff', fontSize: 12, marginLeft: 6 }}>ⓘ</span>}
                    </span>
                  </Tooltip>
                )}
              </div>
              {/* horizon + agreement */}
              <div style={{ fontSize: 12, textAlign: 'right' }}>
                {a.horizon && (
                  <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                           title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                             {`Stays valid for roughly ${HORIZON_PLAIN[a.horizon] || a.horizon}.\n\n`
                              + `How this is set: it is FIXED for this dial, not a judgement — ${AXIS_LABELS[ax]} runs on `
                              + `${AXIS_HORIZON_WHY[ax] || 'its own data cadence'}, so that is how fast the reading goes stale.`}
                           </span>}>
                    <Tag color={HORIZON_COLOR[a.horizon] || 'default'} style={{ marginRight: 0, cursor: 'help' }}>⏱{a.horizon}</Tag>
                  </Tooltip>
                )}
                {agree && (
                  <div style={{ marginTop: 4 }}>
                    <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                             title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                               {`${agree.tip}\n\nMarket Status is the separate equity-regime read (BULL / NEUTRAL / BEAR). `
                                + 'Only Growth and Volatility are comparable to it — the other dials measure something '
                                + 'else, so they get no verdict.'}
                             </span>}>
                      <Tag color={agree.color} style={{ marginRight: 0, cursor: 'help' }}>{agree.mark} {agree.text}</Tag>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', margin: '2px 0 12px' }}>
        Each row shows the signals behind the reading, how many fired, how long it stays valid (⏱),
        and whether it agrees with Market Status.
        {!expanded && ' Hover a row for the detail, or open the full view.'}
        {onOpenMarketStatus && (
          <a onClick={onOpenMarketStatus} style={{ marginLeft: 6, whiteSpace: 'nowrap' }}>View Market Status →</a>
        )}
      </Text>

      {/* DAS-2660: non-axis context reads (institutional positioning) — directional
          colour, not scored. Read from context_json. */}
      {ctx && (ctx.institutional_positioning || ctx.institutional_activity ||
               ctx.institutional_flow || ctx.sector_breadth || ctx.sector_tilt) && (
        <div style={{ margin: '8px 0 12px', fontSize: 13, lineHeight: 2 }}>
          <Text type="secondary" style={{ fontSize: 12, marginRight: 6 }}>
            Context (not scored):
            <InfoTip title={'Background readings that colour the picture but deliberately DO NOT vote on any of the six dials above.\n\nThey are kept separate on purpose: they describe what big investors are doing and how broad the market move is. Letting them also score an axis would count the same information twice.\n\nSo: read these for context, not as part of the regime call. Hover each badge for how it is worked out.'} />
          </Text>
          {ctx.institutional_positioning && (
            <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                     title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                       {'Whether big investors are net long or net short overall.\n\nHow: read straight from the stored `overall_trend` positioning reading — no maths applied here.\n\nContext only: it does not vote on any dial.'}
                     </span>}>
              <Tag color="blue" style={{ marginRight: 6, cursor: 'help' }}>
                Institutional positioning: <b>{ctx.institutional_positioning}</b>
              </Tag>
            </Tooltip>
          )}
          {ctx.institutional_activity && (
            <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                     title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                       {'Are institutions buying or selling the big index ETFs (SPY / QQQ / IWM)?\n\nHow: read straight from the stored `institutional_trend` reading — Buying, Selling or Mixed.\n\nContext only: it does not vote on any dial.'}
                     </span>}>
              <Tag style={{ marginRight: 6, cursor: 'help' }}>ETF activity: <b>{ctx.institutional_activity}</b></Tag>
            </Tooltip>
          )}
          {/* DAS-2647: sector breadth/tilt + institutional flow (structured context). */}
          {ctx.institutional_flow && (
            <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                     title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                       {'Money flowing IN versus OUT across the region and sector ETFs.\n\nHow: every flow reading is bucketed into buying, selling or neutral, then simply counted. The badge shows those counts — e.g. "0 accumulating / 1 distributing / 40 neutral". If nothing is buying or selling it reads "institutions on the sidelines".\n\nContext only: it does not vote on any dial.'}
                     </span>}>
              <Tag color="purple" style={{ marginRight: 6, cursor: 'help' }}>Institutional flow: <b>{ctx.institutional_flow}</b></Tag>
            </Tooltip>
          )}
          {ctx.sector_breadth && (
            <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                     title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                       {'How WIDE the move is — how many of the 11 sectors are trending up versus down.\n\nHow: each sector\'s trend direction is classed bullish or bearish and the two are counted, e.g. "7/11 sectors bullish, 0 bearish". Sectors that are neither are left out of both counts, so the numbers need not add to 11.\n\nWhy it matters: an index at record highs on only a few sectors is narrower — and more fragile — than one carried by most of them.'}
                     </span>}>
              <Tag color="geekblue" style={{ marginRight: 6, cursor: 'help' }}>Sector breadth: <b>{ctx.sector_breadth}</b></Tag>
            </Tooltip>
          )}
          {ctx.sector_tilt && (
            <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 440 }}
                     title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                       {'Are investors leaning into risk or hiding from it?\n\nHow: average bullishness of the cyclical sectors (the ones that do well when the economy runs hot) minus the same average for the defensive sectors. More than 8 points ahead → "cyclicals leading (risk-on)"; more than 8 behind → "defensives leading (risk-off)"; anything in between → "balanced".\n\nContext only: it does not vote on any dial.'}
                     </span>}>
              <Tag color="cyan" style={{ marginRight: 0, cursor: 'help' }}>Rotation: <b>{ctx.sector_tilt}</b></Tag>
            </Tooltip>
          )}
        </div>
      )}

      {/* DAS-2664: Forward risks (watchlist) — the owner's supervision surface over
          the DAS-2663 dynamic registry. Direction + coarse tier only, NO percentages.
          New risks auto-activate; the Retire button is the kill switch. */}
      {scen && (scen.registry || []).some((r) => r.status === 'active' || r.status === 'proposed') && (() => {
        const scores = {};
        (scen.latest_scores || []).forEach((s) => { scores[s.slug] = s; });
        const prevScores = {};
        ((prev && prev.scenarios_json) || []).forEach((s) => { prevScores[s.slug] = s; });
        const DIR = {
          rising:   { arrow: '↑', color: 'volcano', tip: 'Risk is building' },
          stable:   { arrow: '→', color: 'blue',    tip: 'Unchanged — live watch item' },
          easing:   { arrow: '↓', color: 'green',   tip: 'Risk is fading' },
          obsolete: { arrow: '✕', color: 'default', tip: 'Resolved / no longer market-relevant — retires soon' },
        };
        const dirRank = { rising: 0, stable: 1, easing: 2, obsolete: 3 };
        const rows = (scen.registry || [])
          .filter((r) => r.status === 'active' || r.status === 'proposed')
          .sort((a, b) => (dirRank[scores[a.slug]?.direction] ?? 4) - (dirRank[scores[b.slug]?.direction] ?? 4));
        return (
          <div style={{ margin: '4px 0 12px' }}>
            <Text strong style={{ fontSize: 13 }}>Forward risks (watchlist)</Text>
            <InfoTip title={'Named things that could go wrong, tracked over time. Each one keeps its identity between runs, so "is this getting worse or better?" stays meaningful.\n\nWHAT THE ARROWS AND COLOURS MEAN\n↑ orange — risk is building\n→ blue — unchanged, still a live watch item\n↓ green — risk is fading\n✕ grey — resolved or no longer market-relevant; retires soon\n\nTHE SMALL BADGES\n• plausible / likely / … — the coarse tier. Deliberately no percentages: a made-up probability would look more precise than the evidence is.\n• "was rising" (gold) — the direction CHANGED since the previous run. That is the one to notice.\n• "auto-added" — the daily analysis found this itself. Retire it if you disagree.\n• "new — first score next run" — added but not yet scored.\n\nTHE SECTOR CHIPS\nRed with ↓ = sectors expected to SUFFER if this risk plays out.\nGreen with ↑ = sectors expected to BENEFIT.\n\nRetire removes a risk from the watchlist for good.'} />
            <Text type="secondary" style={{ fontSize: 11.5, marginLeft: 8 }}>
              re-scored daily with sources · new risks auto-added by the analysis · no probabilities, direction only
            </Text>
            {rows.map((r) => {
              const sc = scores[r.slug];
              const d = sc ? DIR[sc.direction] : null;
              const was = prevScores[r.slug];
              const changed = sc && was && was.direction !== sc.direction;
              const pb = r.sector_playbook_json || {};
              const detail = [
                sc?.why || '',
                (sc?.sources || []).length ? `sources: ${sc.sources.join(' · ')}` : '',
                r.description || '',
              ].filter(Boolean).join('\n\n');
              return (
                <div key={r.slug} style={{
                  display: 'grid', gridTemplateColumns: '190px minmax(0,1fr) 70px', gap: 10,
                  alignItems: 'start', padding: '7px 0', borderTop: '1px solid #f0f0f0',
                }}>
                  <div>
                    {d ? (
                      <Tooltip zIndex={TIP_Z} title={`${d.arrow} ${d.tip}`}>
                        <Tag color={d.color} style={{ marginRight: 0, whiteSpace: 'normal', cursor: 'help' }}>
                          {d.arrow} {r.name}
                        </Tag>
                      </Tooltip>
                    ) : (
                      <Tag style={{ marginRight: 0, whiteSpace: 'normal' }}>{r.name}</Tag>
                    )}
                    <div style={{ marginTop: 3, fontSize: 11 }}>
                      {sc?.tier && (
                        <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 400 }}
                                 title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                                   {`How seriously to take this risk: "${sc.tier}".\n\nA coarse tier on purpose — no percentage is given, because a number would suggest more precision than the evidence supports.`}
                                 </span>}>
                          <Tag style={{ fontSize: 10, marginRight: 4, cursor: 'help' }}>{sc.tier}</Tag>
                        </Tooltip>
                      )}
                      {changed && (
                        <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 400 }}
                                 title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>
                                   {`Direction CHANGED since the previous run: it was "${was.direction}", now "${sc.direction}".\n\nThis is the badge to notice — the risk itself moved, not just its wording.`}
                                 </span>}>
                          <Tag color="gold" style={{ fontSize: 10, marginRight: 4, cursor: 'help' }}>was {was.direction}</Tag>
                        </Tooltip>
                      )}
                      {!sc && <Tag color="purple" style={{ fontSize: 10, marginRight: 4 }}>new — first score next run</Tag>}
                      {r.proposed_by === 'llm' && (
                        <Tooltip zIndex={TIP_Z} title="Discovered and added by the daily analysis — retire it if you disagree.">
                          <Tag style={{ fontSize: 10, marginRight: 0, cursor: 'help' }}>auto-added</Tag>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 12.5, minWidth: 0 }}>
                    {expanded ? (
                      <>
                        {sc?.why && <div>{sc.why}</div>}
                        {(pb.losers || []).length > 0 && (
                          <Tooltip zIndex={TIP_Z} title="Sectors expected to SUFFER if this risk plays out.">
                            <span>{(pb.losers || []).map((s) => <Tag key={s} color="red" style={{ fontSize: 10, cursor: 'help' }}>{s} ↓</Tag>)}</span>
                          </Tooltip>
                        )}
                        {(pb.winners || []).length > 0 && (
                          <Tooltip zIndex={TIP_Z} title="Sectors expected to BENEFIT if this risk plays out.">
                            <span>{(pb.winners || []).map((s) => <Tag key={s} color="green" style={{ fontSize: 10, cursor: 'help' }}>{s} ↑</Tag>)}</span>
                          </Tooltip>
                        )}
                        {(sc?.sources || []).length > 0 && (
                          <div><Text type="secondary" style={{ fontSize: 11, overflowWrap: 'anywhere' }}>
                            sources: {sc.sources.join(' · ')}
                          </Text></div>
                        )}
                      </>
                    ) : (
                      <Tooltip zIndex={TIP_Z} overlayStyle={{ maxWidth: 460 }}
                               title={detail ? <span style={{ whiteSpace: 'pre-line' }}>{detail}</span> : ''}>
                        <span style={{ cursor: detail ? 'help' : 'default' }}>
                          {sc?.why && <span>{sc.why}</span>}
                          {detail && <span style={{ color: '#1677ff', fontSize: 12, marginLeft: 6 }}>ⓘ</span>}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    {r.status === 'proposed' ? (
                      <Space direction="vertical" size={2}>
                        <Button size="small" type="link" style={{ padding: 0, height: 18 }}
                                onClick={() => decideScenario(r.id, 'confirm')}>confirm</Button>
                        <Button size="small" type="link" danger style={{ padding: 0, height: 18 }}
                                onClick={() => decideScenario(r.id, 'reject')}>reject</Button>
                      </Space>
                    ) : (
                      <Popconfirm title="Remove this risk from the watchlist?"
                                  onConfirm={() => decideScenario(r.id, 'retire')}
                                  okText="Retire" cancelText="Keep">
                        <Button size="small" type="link" danger style={{ padding: 0, height: 18 }}>retire</Button>
                      </Popconfirm>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {data.changes_since_last && (
        <Alert
          style={{ marginTop: 8 }}
          type="warning"
          message="What changed since last run"
          description={stripCitations(data.changes_since_last)}
          showIcon
        />
      )}

      {/* Sources (provenance) */}
      <Title level={5} style={{ marginTop: 16 }}>Sources</Title>
      {sources.length === 0 ? <Text type="secondary">No sources recorded.</Text> : (
        <ul style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{sources.map((s, i) => (
          <li key={i}>
            <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>{stripCitations(s.title) || s.url}</a>
            {s.used_for ? <Text type="secondary"> — {stripCitations(s.used_for)}</Text> : null}
          </li>
        ))}</ul>
      )}

      {/* Cost + telemetry */}
      <Descriptions size="small" column={2} style={{ marginTop: 12 }} bordered>
        <Descriptions.Item label="Generation cost">
          €{Number(data.cost_eur ?? 0).toFixed(4)} (${Number(data.cost_usd ?? 0).toFixed(4)})
        </Descriptions.Item>
        {/* DAS-2801: both of these were a bare "—", which reads as broken. They are
            empty because the nightly run goes through the Claude SUBSCRIPTION (the
            `claude` command-line tool), whose reply reports cost and tokens but not
            which model answered nor how many web searches it ran. The API-key path
            does report both — hence the columns. Say that instead of showing a dash. */}
        <Descriptions.Item label="Model">
          {data.llm_model || (
            <Text type="secondary">
              not reported
              <InfoTip title={'Empty because this run used the Claude subscription rather than the pay-per-use API key.\n\nThe subscription runs through the `claude` command-line tool, and its reply does not say which model answered — so there is nothing truthful to show. Cost and tokens ARE reported, which is why those are filled in.\n\nRuns made on the API key do fill this in.'} />
            </Text>
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Tokens (in / out)">
          {(data.input_tokens ?? '—')} / {(data.output_tokens ?? '—')}
        </Descriptions.Item>
        <Descriptions.Item label="Web searches">
          {data.web_search_count ?? (
            <Text type="secondary">
              not reported
              <InfoTip title={'Empty for the same reason as Model: on the subscription path the `claude` command-line tool does not report how many web searches it ran.\n\nThe research definitely happened — the Sources list above it is the evidence. Only the COUNT is unavailable.\n\nRuns made on the API key do fill this in.'} />
            </Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      {/* Prompt-ready block (reusable in any LLM context) */}
      {data.prompt_ready_text && (
        <div style={{ marginTop: 12 }}>
          <Text strong style={{ fontSize: 13 }}>Market summary for pasting into a chat</Text>
          <InfoTip title={'The block below is the whole Broad Picture squeezed into a short, plain summary — built from the computed numbers, NOT written by the model.\n\nWHY IT EXISTS: other parts of the dashboard paste this same text in when they ask an AI something (the false-positive checker, the portfolio analysis, Select Now). That way every one of them reasons from the same market backdrop instead of each forming its own view.\n\nWHAT THE BUTTON DOES: copies that text to your clipboard, so you can paste it into ChatGPT/Claude yourself and ask your own question with today\'s market context already filled in.\n\nYou never have to use it — it is a convenience, and the text is shown below so you can read it without copying.'} />
          <div style={{ marginTop: 6 }}>
            <Button size="small" onClick={() => {
              navigator.clipboard?.writeText(stripCitations(data.prompt_ready_text));
              message.success('Prompt-ready Broad Picture copied');
            }}>Copy prompt-ready text</Button>
          </div>
          <Paragraph code style={{ whiteSpace: 'pre-wrap', marginTop: 8, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
            {stripCitations(data.prompt_ready_text)}
          </Paragraph>
        </div>
      )}
    </>
  );

  return (
    <>
      <Card
        title={
          <span onClick={() => setModalOpen(true)} style={{ cursor: 'pointer' }} title="Open full view">
            🌍 Broad Picture — <Text strong>{stripCitations(data.regime_label) || 'Macro regime'}</Text>
          </span>
        }
        extra={
          <Space size={4}>
            <Button size="small" data-testid="broad-picture-button" onClick={() => setModalOpen(true)}>⛶ Full view</Button>
            {generateButton}
            <Text type="secondary">{fmtEpoch(data.epoch)}</Text>
          </Space>
        }
        style={{ marginBottom: 16, maxWidth: '100%', overflowWrap: 'anywhere' }}
      >
        {renderContent(false)}
      </Card>

      {/* DAS-2645: full-view modal (MUI) — always-expanded text + PDF / Create-Jira actions */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth
              PaperProps={{ style: { maxHeight: '92vh' } }}>
        <DialogTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>🌍 Broad Picture — {stripCitations(data.regime_label) || 'Macro regime'}</span>
          <Space size={8}>
            <Button size="small" type="primary" onClick={handleDownloadPDF}>Download PDF</Button>
            <Button size="small" onClick={() => { setJiraDesc(''); setJiraOpen(true); }}>Create Jira ticket</Button>
          </Space>
        </DialogTitle>
        <DialogContent dividers>
          <div id="broad-picture-modal-content" data-testid="broad-picture-modal"
               style={{ background: '#fff', padding: 4 }}>
            {renderContent(true)}
          </div>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setModalOpen(false)}>Close</MuiButton>
        </DialogActions>
      </Dialog>

      {/* DAS-2645: create a Jira ticket from this Broad Picture (optional description). */}
      <Dialog open={jiraOpen} onClose={() => !jiraBusy && setJiraOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Jira ticket from this Broad Picture</DialogTitle>
        <DialogContent dividers>
          <Text type="secondary">Summary: <b>Broad Picture — {stripCitations(data.regime_label) || 'Macro regime'}</b></Text>
          <TextField
            label="Description (optional context to add)"
            multiline minRows={4} fullWidth value={jiraDesc}
            onChange={(e) => setJiraDesc(e.target.value)}
            style={{ marginTop: 12 }}
          />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            The current Broad Picture text is attached to the ticket automatically.
          </Text>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setJiraOpen(false)} disabled={jiraBusy}>Cancel</MuiButton>
          <MuiButton variant="contained" onClick={handleCreateJira} disabled={jiraBusy}>
            {jiraBusy ? 'Creating…' : 'Create ticket'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BroadPicture;
